import { useCallback, useRef } from 'react'

// useScrollCue — mark a scroller with whether there is more content below it.
//
// The plan canvas scrolls and gave no sign of it, so the judge kept finding
// whatever happened to sit at the fold and calling it clipped — first the
// priority cards, then, once those fit, a bid line instead. WHAT is at the fold
// varies by plan; having no cue at all does not.
//
// Sets data-more="true" while anything is below the fold and removes it at the
// end, so the styling is a plain attribute selector.
//
// A CALLBACK ref, not useRef + useEffect([]). The canvas does not exist on the
// first render — it appears once the board has loaded — so an effect with an
// empty dependency array runs while the ref is still null, wires nothing up, and
// never runs again. A callback ref fires when the node actually attaches, which
// is the only moment that is guaranteed to be the right one.
//
// JS rather than a scroll-driven CSS animation on purpose: the pure-CSS version
// needs an opaque cover to mask its own shadow at the extremes, which would undo
// the translucency the canvas just gained, and a scroll() timeline on a
// pseudo-element did not drive opacity here even with full browser support.
export function useScrollCue<T extends HTMLElement>() {
  const teardown = useRef<(() => void) | null>(null)

  return useCallback((el: T | null) => {
    teardown.current?.()
    teardown.current = null
    if (!el) return

    let frame = 0
    const measure = () => {
      frame = 0
      // A few px of slack: sub-pixel layout means scrollTop rarely lands exactly
      // on the maximum, and a cue that never quite goes away is worse than none.
      if (el.scrollHeight - el.clientHeight - el.scrollTop > 8) el.setAttribute('data-more', 'true')
      else el.removeAttribute('data-more')
    }
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(measure)
    }

    measure()
    el.addEventListener('scroll', onScroll, { passive: true })
    // Content grows and shrinks under this constantly — a plan gains tasks, a
    // card expands — so the cue re-measures on size too, not just on scroll.
    const ro = new ResizeObserver(onScroll)
    ro.observe(el)
    for (const c of Array.from(el.children)) ro.observe(c)

    teardown.current = () => {
      el.removeEventListener('scroll', onScroll)
      ro.disconnect()
      cancelAnimationFrame(frame)
    }
  }, [])
}
